import { generateText, jsonSchema, type CoreMessage, type ToolSet } from "ai"
import { loadApiKeys } from "../integrations/channels/cache"
import { createModel } from "../utils"
import { callTool, fail } from "./events"
import { generateTool, pdfTool, searchTool } from "./tools"

const MAX_STEPS = 6
const NO_ARGUMENTS = { type: "object", properties: {} }
const CACHE = { anthropic: { cacheControl: { type: "ephemeral" } } }

// The real PlaybookAI. A transition tool carries no execute, so the SDK stops on the step that calls it and
// hands the call back: that call is the way out. Output tools carry none either, and run here, all at once,
// after the call. Every other tool runs and its result goes back to the model; a pdf also leaves its file url in files
// The team's keys are read once, on the first call, and reused by every call after it. Every tool goes through callTool
export function createAI(team: string): PlaybookAI {
  let keys: Promise<APIKeys | null>

  return async (run, call) => {

    const apiKeys = await (keys ||= loadApiKeys(team))
    if (!apiKeys) throw new Error("Could not load the team's api keys")

    const model = createModel(apiKeys, call.model || "")
    if (!model) throw new Error(`"${call.model}" is not a valid model`)

    const platform = call.platform || []
    const files: string[] = []
    const generators = new Map(platform.filter(t => t.type === "generate").map((t, i) => [`generate_${i + 1}`, t]))
    const all = [
      ...(call.tools || []),
      ...platform.filter(t => t.type === "search").map((t, i) => searchTool(t, i + 1, apiKeys, team)),
      ...platform.filter(t => t.type === "generate").map((t, i) => generateTool(t, i + 1)),
      ...platform.filter(t => t.type === "pdf").map((t, i) => pdfTool(t, i + 1, files)),
    ]

    const tools: ToolSet = {}
    for (const t of all) {
      tools[t.name] = {
        description: t.description,
        parameters: jsonSchema(t.parameters || NO_ARGUMENTS),
        execute: t.run ? args => callTool(run, t.name, args, a => t.run!(a, run)) : undefined,
      }
    }

    // Stable first, so every provider can cache by prefix: the prompt (behind the tools), the context, then the history.
    // Anthropic only caches what is marked, so each of the three ends on a breakpoint; the message is the uncached tail
    const messages: CoreMessage[] = []
    if (call.prompt) messages.push({ role: "system", content: call.prompt, providerOptions: CACHE })
    if (call.context) messages.push({ role: "system", content: call.context, providerOptions: CACHE })
    messages.push(...(call.history || []).map(m => ({ role: m.role, content: m.message })))
    if (call.history?.length) messages[messages.length - 1].providerOptions = CACHE
    messages.push({ role: "user", content: run.input.message || "" })

    const result = await generateText({
      model,
      messages,
      tools,
      maxSteps: MAX_STEPS,
    })

    const transition = result.toolCalls.find(c => call.tools?.find(t => t.name === c.toolName)?.transition)

    const generated = result.toolCalls.filter(c => generators.has(c.toolName))
    const written = await Promise.all(generated.map(async c => {
      const config = generators.get(c.toolName) as Extract<PlaybookToolConfig, { type: "generate" }>
      return callTool(run, c.toolName, c.args, args => write(run, apiKeys, config, call.model, args?.brief))
    }))

    // Every step, not only the last one: the result of a generate is what its writer wrote
    const results = new Map<string, any>(result.steps.flatMap(s => s.toolResults as any[]).map(r => [r.toolCallId, r.result]))
    generated.forEach((c, i) => results.set(c.toolCallId, written[i]))
    const calls = result.steps.flatMap(s => s.toolCalls).map(c => ({ name: c.toolName, args: c.args, result: results.get(c.toolCallId) }))

    const text = [result.text, ...written].filter(Boolean).join("\n\n")
    return { text: text || undefined, transition: transition?.toolName, files: files.length ? files : undefined, calls: calls.length ? calls : undefined }
  }
}

// The writer sees only its own prompt and the brief. A failure drops the text, not the turn
async function write(run: PlaybookRun, keys: APIKeys, config: Extract<PlaybookToolConfig, { type: "generate" }>, fallback: string | undefined, brief: string) {
  const model = createModel(keys, config.model || fallback || "")
  if (!model || !brief) return ""
  try { return (await generateText({ model, system: config.prompt, prompt: brief })).text }
  catch (e) {
    await fail(run, e, "the writer failed")
    return ""
  }
}
